import InfoIcon from "@mui/icons-material/Info";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { Chip, Stack, type Theme, Tooltip, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import chroma from "chroma-js";
import { DateTime } from "luxon";
import type React from "react";
import { useEffect, useState } from "react";

export const MAX_DATE = DateTime.fromISO("9999-12-31T00:00:00Z").startOf("day");

export const calculateDateInfo = (date: string, variant: "fromDate" | "toDate", theme: Theme) => {
	const parsedDate = DateTime.fromISO(date, { locale: "cs" });

	if (!parsedDate.isValid) {
		return {
			color: theme.palette.error.main,
			timeLeftText: "Neplatné datum",
			formattedDate: null,
		};
	}

	if (parsedDate.year === MAX_DATE.year) {
		return {
			color: theme.palette.grey[600],
			timeLeftText: "Bez omezení konce platnosti",
			formattedDate: parsedDate,
		};
	}

	if (variant !== "toDate") {
		return {
			color: theme.palette.grey[600],
			timeLeftText: "",
			formattedDate: parsedDate,
		};
	}

	const publishToDateTime = parsedDate;
	const now = DateTime.now().setLocale("cs");
	const oneDayBefore = publishToDateTime.minus({ days: 1 });
	const oneWeekBefore = publishToDateTime.minus({ weeks: 1 });
	const twoWeeksBefore = publishToDateTime.minus({ weeks: 2 });

	let startColor = "";
	let endColor = "";
	let color = theme.palette.grey[600]; // Default color
	let timeLeftText = ""; // Text displaying how much time is left
	let percentage = 0;

	// More than two weeks before expiration
	if (now < twoWeeksBefore) {
		timeLeftText = ""; // Default message for more than two weeks
	}

	// Calculate days left for determining the text
	const daysLeft = Math.floor(publishToDateTime.diff(now, "days").days);

	// Two weeks to one week before expiration
	if (now >= twoWeeksBefore && now < oneWeekBefore) {
		startColor = theme.palette.grey[600];
		endColor = theme.palette.warning.main;
		const progress = now.diff(twoWeeksBefore).as("seconds");
		const total = oneWeekBefore.diff(twoWeeksBefore).as("seconds");
		percentage = progress / total;
		timeLeftText = `Konec aukce za ${daysLeft} ${daysLeft >= 5 ? "dnů" : daysLeft > 1 ? "dny" : "den"}`;
	}

	// One week to one day before expiration
	if (now >= oneWeekBefore && now < oneDayBefore) {
		startColor = theme.palette.warning.main;
		endColor = theme.palette.primary.main;
		const progress = now.diff(oneWeekBefore).as("seconds");
		const total = oneDayBefore.diff(oneWeekBefore).as("seconds");
		percentage = progress / total;
		timeLeftText = `Konec aukce za ${daysLeft} ${daysLeft >= 5 ? "dnů" : daysLeft > 1 ? "dny" : "den"}`;
	}

	// Last day before expiration, including handling of the "tomorrow" case
	if (now >= oneDayBefore && now < publishToDateTime) {
		startColor = theme.palette.primary.main;
		endColor = theme.palette.error.main;
		const progress = now.diff(oneDayBefore).as("seconds");
		const total = publishToDateTime.diff(oneDayBefore).as("seconds");
		percentage = progress / total;
		if (daysLeft >= 1) {
			// If there's a whole day or more left
			timeLeftText = "Konec aukce již zítra";
		} else {
			// If it's less than a day left
			const hoursLeft = Math.floor(publishToDateTime.diff(now, "hours").hours);
			const minutesLeft = Math.floor(publishToDateTime.diff(now, "minutes").minutes) % 60;
			const secondsLeft = Math.floor(publishToDateTime.diff(now, "seconds").seconds) % 60;
			timeLeftText = `Konec aukce za ${hoursLeft} h ${minutesLeft} m ${secondsLeft} s`;
		}
	}

	if (startColor && endColor) {
		color = interpolateColors(startColor, endColor, percentage);
	}

	return {
		color: color,
		timeLeftText: timeLeftText,
		formattedDate: publishToDateTime,
	};
};

function interpolateColors(color1: string, color2: string, percentage: number) {
	return chroma.mix(color1, color2, percentage).hex();
}

type AdvertisementDateProps = {
	date: string;
	variant: "fromDate" | "toDate";
};

const AdvertisementDate: React.FC<AdvertisementDateProps> = ({ date, variant }) => {
	const theme = useTheme();
	const [timeInfo, setTimeInfo] = useState(() => calculateDateInfo(date, variant, theme));

	useEffect(() => {
		if (variant === "toDate") {
			const intervalId = setInterval(() => {
				setTimeInfo(calculateDateInfo(date, variant, theme));
			}, 1000);
			return () => clearInterval(intervalId);
		}
	}, [date, variant, theme]);

	const { color, timeLeftText, formattedDate } = timeInfo;

	if (!(formattedDate?.isValid)) {
		return (
			<Typography
				fontSize="0.8rem"
				fontWeight="bolder"
				sx={{ color: theme.palette.error.main }}
				variant="subtitle1"
				component="div"
			>
				Neplatné datum
			</Typography>
		);
	}

	return (
		<Stack direction="row" spacing={3}>
			{formattedDate.year !== MAX_DATE.year &&
				timeLeftText === "" && ( // Check to hide the Tooltip if the date is the max date
					<Tooltip placement="top" arrow={true} title={variant === "fromDate" ? "Platnost inzerátu od" : timeLeftText}>
						<Typography fontSize="0.8rem" fontWeight="bolder" sx={{ color }} variant="subtitle1" component="div">
							Konec aukce: {formattedDate.toLocaleString(DateTime.DATE_FULL)}
						</Typography>
					</Tooltip>
				)}
			{variant === "toDate" && timeInfo.formattedDate && timeLeftText !== "" && (
				<Chip
					label={timeLeftText}
					size="small"
					sx={{ color: "white", backgroundColor: color }}
					icon={
						timeLeftText.includes("zítra") || timeLeftText.includes("h") ? (
							<ReportProblemIcon sx={{ paddingLeft: "0.3rem" }} color="inherit" />
						) : (
							<InfoIcon sx={{ paddingLeft: "0.3rem" }} color="inherit" />
						)
					}
				/>
			)}
		</Stack>
	);
};

export default AdvertisementDate;
